import { IsDebug } from 'domain/utils/domainUtil';
import * as S from './styles';
import { StoreAplication } from '../../../main';
import { SetReduxView } from '../../../store/util/action/utilAction';
import { RootStateBase } from '../../../store/baseStore';
import { connectUtil, PropsFromRedux } from '../../../utils/reduxUtil';
import React, { useEffect } from 'react';
import { _UserData } from '../../../infra/storage/localStorage';
import { UsuarioResponse } from 'domain/types/usuario/usuarioResponse';
import { Icon } from '@iconify/react';

export interface ReduxViewProps {
    x: number
    y: number
    state: boolean
}

const connector = connectUtil(
    (state: RootStateBase) => ({
        userData: state.UtilReducer.UserData,
        reduxView: state.UtilReducer.ReduxView,
    }), { SetReduxView }
);

interface Props extends PropsFromRedux<typeof connector> {
}

function ReduxView(props: Props) {
    const [collapsed, setCollapsed] = React.useState<Record<string, boolean>>({});
    const [states, setStates] = React.useState(StoreAplication.getState());
    const [userData, setUserData] = React.useState<UsuarioResponse | null>(null);

    const reduxViewRef = React.useRef<HTMLDivElement | null>(null);
    const headerRef = React.useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const element = reduxViewRef.current;
        if (!element) {
            return;
        }
        const offsetX = parseInt(element.dataset.dragOffsetX || "0", 10);
        const offsetY = parseInt(element.dataset.dragOffsetY || "0", 10);
        element.style.left = `${props.reduxView.x - offsetX}px`;
        element.style.top = `${props.reduxView.y - offsetY}px`;
    }, [props.reduxView.state]);

    useEffect(() => {
        const unsubscribe = StoreAplication.subscribe(() => {
            setStates(StoreAplication.getState());
        });
        return () => unsubscribe();
    }, [StoreAplication.getState()]);

    useEffect(() => {
        refreshUserData()
    }, []);

    function refreshUserData() {
        const userDataAll = _UserData.GetAll();
        setUserData(userDataAll);
    }

    function toggleCollapse(key: string) {
        setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));
    }

    const listReducer = React.useMemo(() => {
        const reducers = Object.entries(states).map(([key, value]) => ({ key, value }));
        return reducers;
    }, [states]);

    function handleDragStart(e: React.MouseEvent<HTMLDivElement>) {
        const element = reduxViewRef.current;
        if (!element) return;
        const rect = element.getBoundingClientRect();
        element.dataset.dragOffsetX = (e.clientX - rect.left).toString();
        element.dataset.dragOffsetY = (e.clientY - rect.top).toString();
    }

    function handleDrag(e: React.MouseEvent<HTMLDivElement>) {
        if (e.buttons !== 1) return; // Only drag with left mouse button
        const element = reduxViewRef.current;
        if (!element) return;
        const offsetX = parseInt(element.dataset.dragOffsetX || "0", 10);
        const offsetY = parseInt(element.dataset.dragOffsetY || "0", 10);
        element.style.left = `${e.clientX - offsetX}px`;
        element.style.top = `${e.clientY - offsetY}px`;
    }

    function handleDragStop(e: React.MouseEvent<HTMLDivElement>) {
        props.SetReduxView({ ...props.reduxView, x: e.clientX, y: e.clientY })
    }

    function handleToggleCollapse(key: string) {
        return () => toggleCollapse(key);
    }

    function handleSubToggleCollapse(key: string) {
        return () => toggleCollapse(key);
    }

    function reduxViewToogle() {
        props.SetReduxView({ ...props.reduxView, state: !props.reduxView.state })
    }
    

    return (
        IsDebug() && props.reduxView.state &&
        <S.ReduxView
            ref={reduxViewRef}
            className="fullCenter"
        >
            <S.ReduxViewHeader
                ref={headerRef}
                onMouseDown={handleDragStart}
                onMouseMove={handleDrag}
                onMouseUp={handleDragStop}
            >
                <S.HeaderTitle>
                    <Icon icon="material-symbols:drag-indicator" width="16" />
                    Redux State
                </S.HeaderTitle>
                <S.HeaderControls>
                    <S.MinimizeButton onClick={reduxViewToogle} title="Minimizar">
                        <Icon icon="material-symbols:minimize" width="16" />
                    </S.MinimizeButton>
                </S.HeaderControls>
            </S.ReduxViewHeader>

            <S.ReduxViewContent>
                {listReducer && listReducer.map((reducer, index) => {
                    const isCollapsed = !collapsed[reducer.key];
                    return (

                        <S.Reducers key={index}>

                            <S.Label>
                                <S.Text>{reducer.key}</S.Text>
                                <S.CollapsibleButton onClick={handleToggleCollapse(reducer.key)}>
                                    {isCollapsed ? '+' : '-'}
                                </S.CollapsibleButton>
                            </S.Label>
                            {!isCollapsed && (
                                <S.Reduce>
                                    {Array.isArray(reducer.value) ? (
                                        reducer.value.map((item, idx) => (
                                            <S.State key={idx}>
                                                <S.Text>{JSON.stringify(item)}</S.Text>
                                            </S.State>
                                        ))
                                    ) : typeof reducer.value === 'object' && reducer.value !== null ? (
                                        Object.entries(reducer.value).map(([key, val], idx) => {
                                            const subKey = `${reducer.key}-${key}`;
                                            const isSubCollapsed = collapsed[subKey];
                                            return (
                                                <S.Reduce key={idx}>
                                                    <S.Text>
                                                        <strong>{key}:</strong>
                                                    </S.Text>
                                                    <S.CollapsibleButton onClick={handleSubToggleCollapse(subKey)}>
                                                        {isSubCollapsed ? '+' : '-'}
                                                    </S.CollapsibleButton>
                                                    {!isSubCollapsed && (
                                                        Array.isArray(val) ? (
                                                            val.map((subItem, subIdx) => (
                                                                <S.State key={subIdx}>
                                                                    <S.Text>{JSON.stringify(subItem)}</S.Text>
                                                                </S.State>
                                                            ))
                                                        ) : (
                                                            <S.State>
                                                                <S.Text>{JSON.stringify(val)}</S.Text>
                                                            </S.State>
                                                        )
                                                    )}
                                                </S.Reduce>
                                            );
                                        })
                                    ) : (
                                        <S.State>
                                            <S.Text>{JSON.stringify(reducer.value)}</S.Text>
                                        </S.State>
                                    )}
                                </S.Reduce>
                            )}

                        </S.Reducers>

                    );
                })}
                
                <S.Reducers>
                    <S.Label>
                        <S.Text>UserData (LocalStorage)</S.Text>
                        <S.RefreshButton onClick={refreshUserData} title="Atualizar dados do usuário">
                            <Icon icon="material-symbols:refresh" width="14" />
                        </S.RefreshButton>
                        <S.CollapsibleButton onClick={handleToggleCollapse("userData")}>
                            {!collapsed["userData"] ? '+' : '-'}
                        </S.CollapsibleButton>
                    </S.Label>
                    {collapsed["userData"] && (
                        <S.Reduce>
                            {userData && Object.entries(userData).map(([key, value], idx) => (
                                <S.State key={idx}>
                                    <S.Text>
                                        <strong>{key}:</strong> {JSON.stringify(value)}
                                    </S.Text>
                                </S.State>
                            ))}
                        </S.Reduce>
                    )}
                </S.Reducers>
            </S.ReduxViewContent>
        </S.ReduxView>
    );
}

export default connector(ReduxView);